/**
 * Simple Angular controller that is attached to the GridStudents within the index.aspx. The purpose of this is to allow the user to easily
 * deactivate students in bulk.
 */
/* @ngInject */ export function bulkStudentsController(
  $scope,
  Alert,
  BooksSvc,
  BusyScreen,
  uiGridConstants
) {
  const alertTitle = 'Deactivate Students';

  const COLUMNS = [
    { field: 'name', cellTooltip: true },
    {
      field: 'studentID',
      width: 110,
      displayName: 'Student ID',
      cellTooltip: true
    },
    {
      field: 'grade',
      width: 80,
      filter: { condition: uiGridConstants.filter.EXACT }
    },
    {
      field: 'graduated',
      width: 100,
      cellTemplate:
        '<div class="ui-grid-cell-contents">{{COL_FIELD == null ? "" : COL_FIELD ? "Yes" : "No"}}</div>',
      filter: {
        type: uiGridConstants.filter.SELECT,
        selectOptions: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' }
        ]
      }
    },
    {
      field: 'positiveBalance',
      width: 120,
      displayName: 'Balance',
      cellTemplate:
        '<div class="ui-grid-cell-contents">{{COL_FIELD > 0 ? "Positive" : COL_FIELD < 0 ? "Negative" : "None"}}</div>',
      filter: {
        type: uiGridConstants.filter.SELECT,
        condition: uiGridConstants.filter.EXACT,
        selectOptions: [
          { value: 1, label: 'Positive' },
          { value: 0, label: 'None' },
          { value: -1, label: 'Negative' }
        ]
      }
    },
    {
      field: 'active',
      width: 80,
      cellTemplate:
        '<div class="ui-grid-cell-contents">{{COL_FIELD ? "Yes" : "No"}}</div>',
      filter: {
        type: uiGridConstants.filter.SELECT,
        selectOptions: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' }
        ]
      }
    }
  ];

  const gridOptions = {
    data: 'dbStudents',
    enableColumnMenus: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableFiltering: true,
    enableRowSelection: true,
    enableSelectAll: true,
    minRowsToShow: 3,
    multiSelect: true,
    enableFullRowSelection: true,
    enableColumnResizing: true,
    showGridFooter: true,
    /**
     * We register the grid api to the $scope so we have reference to which rows are clicked and that way we can interact with them in this file.
     */
    onRegisterApi: function (gridApi) {
      $scope.gridApi = gridApi;

      gridApi.selection.on.rowSelectionChanged($scope, function (row) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifySingleSelect(selectedRows.length, row, alertTitle, 1000);
      });
      gridApi.selection.on.rowSelectionChangedBatch($scope, function (rows) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifyMultiSelect(selectedRows.length, rows, alertTitle, 1000);
      });
    },
    columnDefs: COLUMNS
  };

  /**
   * Our grid options for the Students are named studentGridOptions, so in order for this to interact with the grid we need to set that within
   * the $scope.
   */
  $scope.studentGridOptions = gridOptions;

  /**
   * Takes the list of selected rows and maps them to their ID's. This filters out any students that have already been deactivated and
   * only deactivates students that have not been deactivated already.
   *
   * @returns void
   */
  function deactivateStudents() {
    var selectedRows = $scope.gridApi.selection.getSelectedRows();

    if (!selectedRows.length) {
      Alert.info(
        'No students have been selected. Please select one or more before continuing.',
        alertTitle
      );
      return;
    }

    /**
     * Since the screen shows inactive students as well we need to make sure that we filter out any selected students that are
     * already inactive. This avoids extra resources from being used when we try to deactivate a student that is already inactive.
     */
    const studentsToDeactivate = selectedRows
      .filter((student) => student.active)
      .map((student) => student.id);

    if (!studentsToDeactivate.length) {
      Alert.info(
        'The selected student(s) are already deactivated. Please select a different student(s).',
        alertTitle
      );
      return;
    }

    BusyScreen.setBusy(true);

    BooksSvc.bulkDeactivateStudents(studentsToDeactivate)
      .then(() => BooksSvc.getBulkStudents())
      .then((resp) => {
        // Refresh the purchase order data now that we have closed PO's
        $scope.dbStudents = resp.data.sortOn('name');
        BusyScreen.setBusy(false);
        Alert.success(
          'The selected student(s) have been deactivated.',
          alertTitle
        );
      })
      .catch((error) => {
        BusyScreen.setBusy(false);

        const errorMesssage =
          error.parsedResponse && error.parsedResponse.message()
            ? error.parsedResponse.message()
            : 'There was an error deactivating student(s). Please try again.';

        Alert.error(errorMesssage, alertTitle);
      });
  }

  /**
   * Simple function that checks to see if there are any selected rows. If there are then we can save, if not then we cannot save.
   * If there are no selected rows then the user has not selected anything to save.
   *
   * @returns boolean (true = can Save; false = cannot save)
   */
  function canSaveStudents() {
    return $scope.gridApi.selection.getSelectedRows().length > 0;
  }

  /**
   * Attach these to the $scope so they can be used in the index file.
   */
  $scope.deactivateStudents = deactivateStudents;
  $scope.canSaveStudents = canSaveStudents;
}
